/**
 * WithdrawalRejectionDropdowns — iter-206.
 *
 * Manages the "withdrawal rejection reason" dropdown items shown to the admin
 * when rejecting a merchant withdrawal request.
 *
 * Reads/writes: /api/admin/dropdown-lists (list_type = "withdrawal_rejection")
 * AI translate:  /api/admin/dropdown-lists/translate
 *
 * Bilingual (AR + EN) with a Gemini-powered translate button (uses the user's
 * own GEMINI_API_KEY on the backend — never Emergent LLM Key).
 *
 * NOTE — data flow to merchant platform:
 *   The rejection reason (ar + en) and free-text notes chosen by the admin are
 *   persisted onto the `withdrawal_requests` document itself. Merchant platform
 *   reads them from the same doc (see ITER206 companion patch).
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Plus, Loader2, RefreshCw, Trash2, Archive, ArchiveRestore, Sparkles,
} from 'lucide-react';

import { useLanguage } from '../../context/LanguageContext';
import DashboardHeader from '../../components/DashboardHeader';
import BreadcrumbNav from '../../components/BreadcrumbNav';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { getAuthHeaders } from '../../utils/authHeaders';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const LIST_TYPE = 'withdrawal_rejection';

export default function WithdrawalRejectionDropdowns() {
  const { language } = useLanguage();
  const isAr = language === 'ar';
  const t = (ar, en) => (isAr ? ar : en);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);

  const [creating, setCreating] = useState(false);
  const [newDraft, setNewDraft] = useState({ label_ar: '', label_en: '' });
  const [translating, setTranslating] = useState({ ar: false, en: false });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { list_type: LIST_TYPE, include_archived: showArchived };
      const r = await axios.get(`${API_URL}/api/admin/dropdown-lists`,
        { headers: getAuthHeaders(), params });
      setItems(r.data.data || []);
    } catch {
      toast.error(t('فشل التحميل', 'Load failed'));
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showArchived]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => items, [items]);

  const translateField = async (from) => {
    // from='ar' → translate ar to en; from='en' → translate en to ar
    const srcText = from === 'ar' ? newDraft.label_ar : newDraft.label_en;
    if (!srcText.trim()) {
      toast.error(t('أدخل النص المصدر أولاً', 'Enter source text first'));
      return;
    }
    setTranslating((s) => ({ ...s, [from]: true }));
    try {
      const direction = from === 'ar' ? 'ar_to_en' : 'en_to_ar';
      const r = await axios.post(`${API_URL}/api/admin/dropdown-lists/translate`,
        { text: srcText.trim(), direction },
        { headers: getAuthHeaders() });
      const translated = r.data?.translation || '';
      if (from === 'ar') {
        setNewDraft((d) => ({ ...d, label_en: translated }));
      } else {
        setNewDraft((d) => ({ ...d, label_ar: translated }));
      }
      toast.success(t('✓ تمّت الترجمة', '✓ Translated'));
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل الترجمة', 'Translate failed'));
    } finally {
      setTranslating((s) => ({ ...s, [from]: false }));
    }
  };

  const create = async () => {
    if (!newDraft.label_ar.trim() || !newDraft.label_en.trim()) {
      toast.error(t('الرجاء إدخال الاسم بالعربي والإنجليزي', 'Please enter both AR and EN labels'));
      return;
    }
    setCreating(true);
    try {
      await axios.post(`${API_URL}/api/admin/dropdown-lists`, {
        list_type: LIST_TYPE,
        platform: 'all', // withdrawal rejection reasons are shared across all platforms
        label_ar: newDraft.label_ar.trim(),
        label_en: newDraft.label_en.trim(),
        sort_order: (items[items.length - 1]?.sort_order || 100) + 10,
      }, { headers: getAuthHeaders() });
      toast.success(t('✓ تمت الإضافة', '✓ Added'));
      setNewDraft({ label_ar: '', label_en: '' });
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل الإضافة', 'Add failed'));
    } finally {
      setCreating(false);
    }
  };

  const toggleArchive = async (it) => {
    try {
      await axios.post(`${API_URL}/api/admin/dropdown-lists/${it.id}/archive`, {},
        { headers: getAuthHeaders() });
      toast.success(it.archived ? t('تمت الاستعادة', 'Restored') : t('تمت الأرشفة', 'Archived'));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل التحديث', 'Update failed'));
    }
  };

  const remove = async (it) => {
    if (!window.confirm(t('حذف نهائي لهذا العنصر؟', 'Permanently delete this item?'))) return;
    try {
      await axios.delete(`${API_URL}/api/admin/dropdown-lists/${it.id}`,
        { headers: getAuthHeaders() });
      toast.success(t('تم الحذف', 'Deleted'));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || t('فشل الحذف', 'Delete failed'));
    }
  };

  return (
    <div className="min-h-screen bg-[#030303] relative" data-testid="withdrawal-rejection-dropdowns-page">
      <div className="fixed inset-0 pointer-events-none">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-[800px] h-[500px] bg-red-500/5 rounded-full blur-[150px]" />
      </div>

      <DashboardHeader showBackButton backPath="/dashboard/finance/settings/dropdown-lists" />

      <div className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3">
          <BreadcrumbNav items={[
            { label_ar: 'لوحة التحكم', label_en: 'Dashboard', path: '/dashboard' },
            { label_ar: 'المالية', label_en: 'Finance', path: '/dashboard/finance' },
            { label_ar: 'إعدادات القسم المالي', label_en: 'Finance Settings', path: '/dashboard/finance/settings' },
            { label_ar: 'القوائم المنسدلة', label_en: 'Dropdown Lists', path: '/dashboard/finance/settings/dropdown-lists' },
            { label_ar: 'أسباب رفض طلبات السحب', label_en: 'Withdrawal Rejection Reasons', path: null },
          ]} />
        </div>
      </div>

      <main className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className={`mb-8 ${isAr ? 'text-right' : 'text-left'}`}>
          <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
            {t('أسباب رفض طلبات السحب', 'Withdrawal Rejection Reasons')}
          </h1>
          <p className="text-white/50 text-sm max-w-2xl">
            {t('إدارة قائمة الأسباب التي تظهر للأدمن عند رفض طلب سحب — السبب المختار + الملاحظات ينعكسان تلقائياً على منصة التجار.',
               'Manage the reasons shown when rejecting a merchant withdrawal. Selected reason + notes are auto-reflected back to the merchant platform.')}
          </p>
        </div>

        {/* Add new */}
        <div className="bg-white/[0.02] border border-white/10 rounded-2xl p-5 mb-6"
             data-testid="wd-rej-add-panel">
          <h3 className="text-white font-bold mb-3 text-sm">{t('إضافة سبب جديد', 'Add new reason')}</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* AR field */}
            <div>
              <label className="block text-white/60 text-[11px] mb-1">{t('الاسم بالعربي', 'Arabic label')}</label>
              <div className="flex gap-2">
                <Input
                  value={newDraft.label_ar}
                  onChange={(e) => setNewDraft(d => ({ ...d, label_ar: e.target.value }))}
                  placeholder={t('مثال: IBAN غير صالح', 'e.g. IBAN غير صالح')}
                  dir="rtl"
                  className="bg-[#050505] border-white/10 text-white h-10 flex-1"
                  data-testid="wd-rej-new-label-ar"
                />
                <Button
                  type="button"
                  onClick={() => translateField('ar')}
                  disabled={translating.ar || !newDraft.label_ar.trim()}
                  title={t('ترجمة تلقائية للإنجليزي', 'Auto-translate to English')}
                  className="h-10 px-2 bg-cyan-600/20 hover:bg-cyan-600/30 text-cyan-200 border border-cyan-500/30"
                  data-testid="wd-rej-translate-to-en"
                >
                  {translating.ar
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <Sparkles className="w-4 h-4" />}
                </Button>
              </div>
            </div>
            {/* EN field */}
            <div>
              <label className="block text-white/60 text-[11px] mb-1">{t('الاسم بالإنجليزي', 'English label')}</label>
              <div className="flex gap-2">
                <Input
                  value={newDraft.label_en}
                  onChange={(e) => setNewDraft(d => ({ ...d, label_en: e.target.value }))}
                  placeholder="e.g. Invalid IBAN"
                  dir="ltr"
                  className="bg-[#050505] border-white/10 text-white h-10 flex-1"
                  data-testid="wd-rej-new-label-en"
                />
                <Button
                  type="button"
                  onClick={() => translateField('en')}
                  disabled={translating.en || !newDraft.label_en.trim()}
                  title={t('ترجمة تلقائية للعربي', 'Auto-translate to Arabic')}
                  className="h-10 px-2 bg-cyan-600/20 hover:bg-cyan-600/30 text-cyan-200 border border-cyan-500/30"
                  data-testid="wd-rej-translate-to-ar"
                >
                  {translating.en
                    ? <Loader2 className="w-4 h-4 animate-spin" />
                    : <Sparkles className="w-4 h-4" />}
                </Button>
              </div>
            </div>
          </div>
          <div className="mt-3 flex items-center gap-2">
            <Button
              onClick={create}
              disabled={creating}
              className="bg-[#d4af37] hover:bg-[#d4af37]/90 text-black font-bold h-10 px-4"
              data-testid="wd-rej-new-submit"
            >
              {creating
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <><Plus className="w-4 h-4 me-1" />{t('إضافة السبب', 'Add reason')}</>}
            </Button>
            <span className="text-[10px] text-cyan-300/70 inline-flex items-center gap-1">
              <Sparkles className="w-3 h-3" />
              {t('اكتب بلغة واحدة واضغط ✨ لتترجم الأخرى تلقائياً', 'Type in one language and click ✨ to auto-translate the other')}
            </span>
          </div>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3 mb-4 flex-wrap">
          <label className="inline-flex items-center gap-2 text-white/60 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
              className="accent-[#d4af37]"
              data-testid="wd-rej-show-archived"
            />
            {t('إظهار المؤرشفة', 'Show archived')}
          </label>
          <div className="flex-1" />
          <Button onClick={load} variant="outline" size="sm"
                  className="border-white/10 bg-transparent text-white/80 hover:bg-white/5"
                  data-testid="wd-rej-refresh">
            <RefreshCw className={`w-4 h-4 me-1 ${loading ? 'animate-spin' : ''}`} />
            {t('تحديث', 'Refresh')}
          </Button>
        </div>

        {/* Items table */}
        <div className="bg-white/[0.02] border border-white/10 rounded-2xl overflow-hidden">
          {loading ? (
            <div className="p-16 flex justify-center">
              <Loader2 className="w-6 h-6 animate-spin text-white/40" />
            </div>
          ) : filtered.length === 0 ? (
            <div className="p-12 text-center text-white/40 text-sm">
              {t('لا توجد أسباب بعد. أضِف أول سبب من الأعلى.',
                'No reasons yet. Add your first reason above.')}
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className="text-white/50 text-xs border-b border-white/5">
                  <th className="text-start p-3 font-medium">#</th>
                  <th className="text-start p-3 font-medium">{t('الاسم عربي', 'Arabic')}</th>
                  <th className="text-start p-3 font-medium">{t('الاسم إنجليزي', 'English')}</th>
                  <th className="text-start p-3 font-medium">{t('تاريخ الإضافة', 'Added')}</th>
                  <th className="text-start p-3 font-medium">{t('الحالة', 'Status')}</th>
                  <th className="text-end p-3 font-medium">{t('إجراءات', 'Actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((it, idx) => (
                  <tr key={it.id}
                      className={`border-b border-white/5 hover:bg-white/[0.02] ${it.archived ? 'opacity-50' : ''}`}
                      data-testid={`wd-rej-row-${it.id}`}>
                    <td className="p-3 text-white/40 text-xs">{idx + 1}</td>
                    <td className="p-3 text-white" dir="rtl">{it.label_ar}</td>
                    <td className="p-3 text-white" dir="ltr">{it.label_en}</td>
                    <td className="p-3 text-white/60 text-xs">
                      {new Date(it.created_at).toLocaleString(isAr ? 'ar-EG' : 'en-GB', {
                        year: '2-digit', month: 'short', day: '2-digit', hour: '2-digit', minute: '2-digit',
                      })}
                    </td>
                    <td className="p-3">
                      {it.archived ? (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-amber-500/10 text-amber-300">
                          {t('مؤرشف', 'Archived')}
                        </span>
                      ) : (
                        <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-300">
                          {t('نشط', 'Active')}
                        </span>
                      )}
                    </td>
                    <td className="p-3">
                      <div className="flex items-center gap-1 justify-end">
                        <button onClick={() => toggleArchive(it)}
                                className="p-1.5 rounded-md hover:bg-white/5 text-white/60"
                                title={it.archived ? t('استعادة', 'Restore') : t('أرشفة', 'Archive')}
                                data-testid={`wd-rej-archive-${it.id}`}>
                          {it.archived ? <ArchiveRestore className="w-4 h-4" /> : <Archive className="w-4 h-4" />}
                        </button>
                        <button onClick={() => remove(it)}
                                className="p-1.5 rounded-md hover:bg-red-500/10 text-red-400/60 hover:text-red-400"
                                title={t('حذف', 'Delete')}
                                data-testid={`wd-rej-delete-${it.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </main>
    </div>
  );
}
